import { MobBottomSheet } from '../../components/mob';
import type { AccessibleProperty } from '../../api/types/property.types';
import './PropertyPickerSheet.css';

interface PropertyPickerSheetProps {
  isOpen: boolean;
  onClose: () => void;
  properties: AccessibleProperty[];
  selectedPropertyId: string | null;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  onRetry: () => void;
  onSelect: (entityId: string) => void;
}

export function PropertyPickerSheet({
  isOpen,
  onClose,
  properties,
  selectedPropertyId,
  isLoading,
  isError,
  errorMessage,
  onRetry,
  onSelect,
}: PropertyPickerSheetProps) {
  const handlePick = (entityId: string) => {
    onSelect(entityId);
    onClose();
  };

  return (
    <MobBottomSheet
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel="Select property"
    >
      <div className="prop-picker">
        <h2 className="prop-picker__title">Select property</h2>

        {isLoading ? (
          <div className="prop-picker__state" role="status" aria-live="polite">
            <div className="prop-picker__spinner" aria-hidden="true" />
            <span>Loading properties…</span>
          </div>
        ) : isError ? (
          <div className="prop-picker__state prop-picker__state--err" role="alert">
            <span>{errorMessage ?? "Couldn't load properties"}</span>
            <button
              type="button"
              className="prop-picker__retry"
              onClick={onRetry}
            >
              Retry
            </button>
          </div>
        ) : properties.length === 0 ? (
          <div className="prop-picker__state">
            <span>No properties available for this account.</span>
          </div>
        ) : (
          <ul className="prop-picker__list" role="listbox" aria-label="Available properties">
            {properties.map((p) => {
              const selected = p.entityId === selectedPropertyId;
              return (
                <li key={p.entityId}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`prop-picker__item${selected ? ' is-selected' : ''}`}
                    onClick={() => handlePick(p.entityId)}
                  >
                    <span className="prop-picker__item-txt">
                      <span className="prop-picker__item-name">{p.name}</span>
                      <span className="prop-picker__item-meta">
                        {p.code}
                        {p.timezone ? ` · ${p.timezone}` : ''}
                      </span>
                    </span>
                    {selected ? (
                      <svg
                        className="prop-picker__check"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <polyline points="4 12 10 18 20 6" />
                      </svg>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </MobBottomSheet>
  );
}
